import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const POLLING_INTERVAL = 20000; // 20 seconds
const API_BASE_URL_CONST = process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

function CustomerListPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const latestCustomersRef = useRef(customers);

  useEffect(() => {
    latestCustomersRef.current = customers;
  }, [customers]);

  const fetchCustomers = useCallback(async (isPoll = false) => {
    if (!isPoll) setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL_CONST}/customers/customers.php`, {
        cache: "no-store",
      });
      const result = await response.json();
      if (result.success) {
        const fetchedCustomers = Array.isArray(result.data) ? result.data : [];
        if (isPoll) {
          const currentCustomerIds = new Set(latestCustomersRef.current.map((c) => c.id));
          const newCustomersFound = fetchedCustomers.filter((c) => !currentCustomerIds.has(c.id));
          newCustomersFound.forEach((newCust) => {
            toast.info(`New customer added: ${newCust.first_name} ${newCust.last_name}`);
          });
        }
        setCustomers(fetchedCustomers);
      } else {
        const errorMessage = result.error || "Failed to fetch customers.";
        if (!isPoll) setError(errorMessage);
        else console.warn("Polling: ", errorMessage);
        if (!isPoll) setCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      const errorMessage = "An unexpected error occurred while fetching customers.";
      if (!isPoll) setError(errorMessage);
      else console.error("Polling error: ", errorMessage);
      if (!isPoll) setCustomers([]);
    } finally {
      if (!isPoll) setLoading(false);
    }
  }, []);

  // Initial fetch + refetch on navigation state
  useEffect(() => {
    fetchCustomers(false);
  }, [location.pathname, location.state?.timestamp, fetchCustomers]);

  // Polling
  useEffect(() => {
    let isMounted = true;
    const poll = () => {
      if (document.hidden || !isMounted) return;
      fetchCustomers(true);
    };
    const intervalId = setInterval(poll, POLLING_INTERVAL);
    const handleVisibilityChange = () => {
      if (!document.hidden && isMounted) poll();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchCustomers]);

  // Handle notifications from create/edit
  useEffect(() => {
    if (location.state?.notificationMessage) {
      const { notificationMessage, notificationType = "success" } = location.state;
      toast[notificationType](notificationMessage);
      const { notificationMessage: _, notificationType: __, timestamp, ...rest } = location.state;
      navigate(location.pathname, { state: rest, replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleDelete = async (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    const name = customer ? `${customer.first_name} ${customer.last_name}` : "this customer";
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_BASE_URL_CONST}/customers/delete.php?id=${customerId}`, {
        method: "DELETE",
      });
      const result = await response.json();
      if (result.success) {
        toast.warn(`Customer "${name}" deleted.`);
        setCustomers((prev) => prev.filter((c) => c.id !== customerId));
      } else {
        toast.error(result.message || "Failed to delete customer.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting customer.");
    }
  };

  if (loading) {
    return <p style={{ color: "#fff", textAlign: "center", marginTop: "50px" }}>Loading customers...</p>;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      <div
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "1100px",
          margin: "0 auto",
          boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(8px)",
        }}
      >
        {error && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid #F87171",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "24px",
              color: "#fff",
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "#F87171", margin: "0 0 8px" }}>Error Loading Customers</h3>
            <p style={{ margin: "0 0 12px" }}>{error}</p>
            <button
              onClick={() => fetchCustomers(false)}
              style={{
                padding: "10px 20px",
                background: "#1E3A8A",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Retry
            </button>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "25px",
          }}
        >
          <h2 style={{ fontSize: "28px", fontWeight: "600", margin: 0 }}>Customer Management</h2>
          <Link
            to="/customers/create"
            style={{
              padding: "10px 18px",
              background: "#1E3A8A",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.95em",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              textDecoration: "none",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              transition: "background 0.2s",
            }}
            onMouseOver={(e) => (e.target.style.background = "#152d6e")}
            onMouseOut={(e) => (e.target.style.background = "#1E3A8A")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
            Add Customer
          </Link>
        </div>

        {customers.length === 0 ? (
          <p style={{ textAlign: "center", fontSize: "1.1em", color: "#ddd" }}>
            No customers found. Start by adding a new one!
          </p>
        ) : (
          <div style={{ overflowX: "auto", willChange: "transform" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "rgba(255, 255, 255, 0.9)",
                borderRadius: "10px",
                overflow: "hidden",
                color: "#1E293B",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                  <th style={{ padding: "14px", textAlign: "left" }}>ID</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Name</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Phone</th>
                  <th style={{ padding: "14px", textAlign: "left" }}>Status</th>
                  <th style={{ padding: "14px", textAlign: "left", minWidth: "190px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr
                    key={customer.id}
                    style={{
                      background: index % 2 === 0 ? "#F8FAFC" : "#FFFFFF",
                      borderBottom: "1px solid #E5E7EB",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#E0F2FE")}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = index % 2 === 0 ? "#F8FAFC" : "#FFFFFF")
                    }
                  >
                    <td style={{ padding: "12px" }}>{customer.id}</td>
                    <td style={{ padding: "12px" }}>
                      {customer.first_name} {customer.last_name}
                    </td>
                    <td style={{ padding: "12px" }}>{customer.phone_number || "N/A"}</td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "0.8em",
                          fontWeight: "600",
                          color: "#fff",
                          backgroundColor: customer.is_active ? "#16A34A" : "#6B7280",
                        }}
                      >
                        {customer.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Link
                        to={`/customers/view/${customer.id}`}
                        style={{
                          ...actionBtnStyle,
                          color: "#0EA5E9",
                          borderColor: "#0EA5E9",
                        }}
                      >
                        View
                      </Link>
                      <Link
                        to={`/customers/edit/${customer.id}`}
                        style={{
                          ...actionBtnStyle,
                          color: "#F59E0B",
                          borderColor: "#F59E0B",
                        }}
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        style={{
                          ...actionBtnStyle,
                          color: "#EF4444",
                          borderColor: "#EF4444",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable action button style
const actionBtnStyle = {
  padding: "5px 10px",
  marginRight: "8px",
  border: "1px solid",
  borderRadius: "4px",
  background: "none",
  cursor: "pointer",
  fontSize: "0.85em",
  fontWeight: "500",
  textDecoration: "none",
  display: "inline-block",
  textAlign: "center",
};

export default CustomerListPage;